import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { authApi } from '@/api/auth'

export default function SetPassword() {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuthStore()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (!isAuthenticated) {
    navigate('/login', { replace: true })
    return null
  }

  const orgSlug = user?.org_slug

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setBusy(true)
    try {
      await authApi.setPassword(password)
      navigate(orgSlug ? `/${orgSlug}` : '/')
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not save your password'
      setError(detail)
      setBusy(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <img src="/logo-dark.png" alt="Managition" className="h-9 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900">Set your password</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a password so you can also sign in with your email in future.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-4">
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}
          <div>
            <label htmlFor="pw" className="block text-sm font-medium text-gray-700">
              New password
            </label>
            <input
              id="pw"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#EC5B2C] focus:outline-none focus:ring-1 focus:ring-[#EC5B2C]"
            />
          </div>
          <div>
            <label htmlFor="pw2" className="block text-sm font-medium text-gray-700">
              Confirm password
            </label>
            <input
              id="pw2"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-[#EC5B2C] focus:outline-none focus:ring-1 focus:ring-[#EC5B2C]"
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="w-full flex justify-center items-center gap-2 rounded-md bg-[#EC5B2C] py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-[#d24e22] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#EC5B2C] disabled:opacity-50"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            {busy ? 'Saving...' : 'Save password'}
          </button>
        </form>
      </div>
    </div>
  )
}
